/**
 * Self-heal for stale code-split chunks after a deploy (public website).
 *
 * After a new deploy, an already-open tab still references OLD hashed chunk
 * files that no longer exist — Firebase serves the SPA fallback instead and
 * the dynamic import fails. Instead of an error page, we show a lightweight
 * "Updating…" overlay and reload once (guarded against loops) so the visitor
 * lands on the fresh version.
 */

const RELOAD_GUARD_KEY = 'chunkReloadAttemptedAt';
const RELOAD_GUARD_WINDOW_MS = 30_000;
const OVERLAY_DWELL_MS = 800;
const OVERLAY_ID = 'live-update-overlay';

export function isDynamicImportError(error: unknown): boolean {
  const msg = error instanceof Error ? error.message : String(error ?? '');
  return /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|failed to load module script|dynamically imported module|loading chunk [\w-]+ failed|loading css chunk|chunkloaderror|expected a javascript.?(?:-or-wasm)?.? module script but the server responded with a mime type/i.test(
    msg
  );
}

/** Vanilla full-screen "updating" overlay — works even when React has crashed. */
export function showUpdateOverlay(): void {
  if (document.getElementById(OVERLAY_ID)) return;
  const overlay = document.createElement('div');
  overlay.id = OVERLAY_ID;
  overlay.setAttribute('role', 'status');
  overlay.style.cssText =
    'position:fixed;inset:0;z-index:2147483647;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:18px;' +
    "background:rgba(255,255,255,0.96);font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;text-align:center;padding:24px";
  const style = document.createElement('style');
  style.textContent =
    '@keyframes luSpin{to{transform:rotate(360deg)}}' +
    `#${OVERLAY_ID} .lu-spinner{width:42px;height:42px;border-radius:50%;border:4px solid #e5e7eb;border-top-color:#6eb041;animation:luSpin .8s linear infinite}`;
  overlay.appendChild(style);
  const spinner = document.createElement('div');
  spinner.className = 'lu-spinner';
  overlay.appendChild(spinner);
  const title = document.createElement('div');
  title.textContent = 'Updating to the latest version';
  title.style.cssText = 'font-size:17px;font-weight:600;color:#1f2937';
  overlay.appendChild(title);
  const sub = document.createElement('div');
  sub.textContent = 'One moment — refreshing the page…';
  sub.style.cssText = 'font-size:14px;color:#6b7280';
  overlay.appendChild(sub);
  document.body.appendChild(overlay);
}

/**
 * Overlay + guarded reload. Returns false when the loop guard suppressed the
 * reload (caller should fall back to its normal error UI).
 */
export function recoverFromStaleChunk(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) || 0);
    if (last && Date.now() - last < RELOAD_GUARD_WINDOW_MS) return false;
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  } catch {
    // sessionStorage unavailable — reload anyway.
  }
  showUpdateOverlay();
  window.setTimeout(() => window.location.reload(), OVERLAY_DWELL_MS);
  return true;
}

/** Install the global stale-chunk handlers. Call once at bootstrap. */
export function installChunkReloadHandler(): void {
  window.addEventListener('vite:preloadError', (event) => {
    event.preventDefault();
    recoverFromStaleChunk();
  });
  window.addEventListener('unhandledrejection', (event) => {
    if (isDynamicImportError(event.reason)) {
      event.preventDefault();
      recoverFromStaleChunk();
    }
  });
}
